import React, { useState, useEffect, useRef } from 'react';

interface ColumnConfigProps {
  index: number;
  column: any;
  handleColumnChange: (index: number, key: string, value: any) => void;
  foreignTables: string[];
}

const ColumnConfig: React.FC<ColumnConfigProps> = ({
  index,
  column,
  handleColumnChange,
  foreignTables,
}) => {
  const [hasError, setHasError] = useState(false);
  const detailsRef = useRef<HTMLDetailsElement>(null);

  const isIdColumn = index === 0;
  const isForeignKey = column.type === 'Relacion';

  useEffect(() => {
    setHasError(!column.name);
  }, [column.name]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitized = e.target.value.replace(/[^a-zA-Z_]/g, '');
    setHasError(!sanitized);
    handleColumnChange(index, 'name', sanitized);
  };

  const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    handleColumnChange(index, 'type', value);
    if (value === 'Relacion') {
      handleColumnChange(index, 'isForeignKey', true);
      handleColumnChange(index, 'isNullable', false);
      handleColumnChange(index, 'isPrimaryKey', false);
      handleColumnChange(index, 'isUnique', false);
    } else {
      handleColumnChange(index, 'isForeignKey', false);
      handleColumnChange(index, 'relatedTable', '');
    }
  };

  const handleSelectTable = (table: string) => {
    handleColumnChange(index, 'relatedTable', table);
    if (detailsRef.current) detailsRef.current.open = false;
  };

  const dataTypes = ['Texto', 'Número', 'Fecha', 'Fecha y Hora', 'Relacion'];

  // ── Columna id: siempre muestra Nulo, Llave Primaria, Índice Único deshabilitados ──
  if (isIdColumn) {
    return (
      <div className="row mb-4">
        <div className="col-md-3">
          <input
            type="text"
            className="form-control"
            value={column.name}
            readOnly
            style={{ backgroundColor: '#e9ecef', cursor: 'not-allowed' }}
          />
        </div>
        <div className="col-md-3">
          <select
            className="form-control"
            value={column.type}
            disabled
            style={{ backgroundColor: '#e9ecef', cursor: 'not-allowed' }}
          >
            {dataTypes.map((type) => (
              <option key={type} value={type}>
                {type === 'Relacion' ? 'Tipo Relación (Llave Foránea)' : type}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-6">
          <div className="d-flex justify-content-around align-items-center">
            <div className="form-check">
              <input type="checkbox" className="form-check-input" checked={false} disabled onChange={() => {}} />
              <label className="form-check-label text-muted">Nulo</label>
            </div>
            <div className="form-check">
              <input type="checkbox" className="form-check-input" checked={true} disabled onChange={() => {}} />
              <label className="form-check-label text-muted">Llave Primaria</label>
            </div>
            <div className="form-check">
              <input type="checkbox" className="form-check-input" checked={true} disabled onChange={() => {}} />
              <label className="form-check-label text-muted">Índice Único</label>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="row mb-4">
      {/* Nombre */}
      <div className="col-md-3">
        <input
          type="text"
          className={`form-control ${hasError ? 'is-invalid' : ''}`}
          placeholder="Nombre de la columna"
          value={column.name}
          onChange={handleNameChange}
        />
        {hasError && <div className="invalid-feedback">Por favor, ingrese un nombre para la columna.</div>}
        <small className="text-muted">Solo letras y guión bajo. Ej: <code>mi_columna</code></small>
      </div>

      {/* Tipo */}
      <div className="col-md-3">
        <select className="form-control" value={column.type} onChange={handleTypeChange}>
          {dataTypes.map((type) => (
            <option key={type} value={type}>
              {type === 'Relacion' ? 'Tipo Relación (Llave Foránea)' : type}
            </option>
          ))}
        </select>
      </div>

      {/* Checkboxes — solo si NO es Relacion */}
      {!isForeignKey && (
        <div className="col-md-6">
          <div className="d-flex justify-content-around align-items-center">
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                checked={column.isNullable}
                onChange={(e) => handleColumnChange(index, 'isNullable', e.target.checked)}
              />
              <label className="form-check-label">Nulo</label>
            </div>
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                checked={column.isUnique}
                onChange={(e) => handleColumnChange(index, 'isUnique', e.target.checked)}
              />
              <label className="form-check-label">Índice Único</label>
            </div>
          </div>
        </div>
      )}

      {/* Lista de tablas — solo si es Relacion */}
      {isForeignKey && (
        <div className="col-md-6">
          <details ref={detailsRef} open={!column.relatedTable}>
            <summary style={{ cursor: 'pointer', fontWeight: 500, marginBottom: '8px', color: '#1a7fd4', userSelect: 'none' }}>
              {column.relatedTable
                ? <>🗄️ <strong>{column.relatedTable}</strong> <span style={{ fontSize: '12px', color: '#888' }}>(click para cambiar)</span></>
                : 'Selecciona la tabla relacionada'}
            </summary>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '8px', maxHeight: '180px', overflowY: 'auto', paddingRight: '4px' }}>
              {foreignTables.length === 0 ? (
                <small className="text-muted">No hay tablas disponibles.</small>
              ) : (
                foreignTables.map((table) => (
                  <div
                    key={table}
                    onClick={() => handleSelectTable(table)}
                    style={{
                      padding: '8px 14px',
                      borderRadius: '8px',
                      border: `1px solid ${column.relatedTable === table ? '#1a7fd4' : '#dee2e6'}`,
                      background: column.relatedTable === table ? '#e8f0fe' : '#fff',
                      cursor: 'pointer',
                      fontWeight: column.relatedTable === table ? 600 : 400,
                      color: column.relatedTable === table ? '#1a7fd4' : '#333',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    🗄️ {table}
                  </div>
                ))
              )}
            </div>
          </details>
        </div>
      )}
    </div>
  );
};

export default ColumnConfig;